import type { PresetBlock } from "../contract/types";
import { Icon, type IconName } from "../components/Icon";
import type { BlockViewProps } from "./types";

/**
 * Vista de pantalla completa, emitida como un bloque más.
 *
 * El backend manda el preset dentro de un `block.add` normal, así que aquí se
 * pinta como cualquier otro bloque: una tarjeta con el nombre de la vista, su
 * título y un botón para abrirla. Esa tarjeta es lo que hace que el preset
 * sobreviva a recargar la conversación — al abrir la vista, el bloque sigue en
 * el stream y se puede volver a entrar sin pedírselo otra vez al agente.
 *
 * La vista en sí la compone `PresetRenderer` a partir de `name` + `data`, y
 * vive en la columna central (`StageView`), no aquí dentro: un preset ocupa la
 * pantalla, y meterlo en una tarjeta del stream sería contradecirse.
 */

const PRESET_LABEL: Record<string, string> = {
  course_overview: "Panorama del curso",
  unit_dashboard: "Tablero de unidad",
  concept_deep_dive: "Concepto a fondo",
  case_study: "Caso del curso",
  assessment_brief: "Ficha de evaluación",
  study_path: "Plan de estudio",
};

const PRESET_ICON: Record<string, IconName> = {
  course_overview: "compass",
  unit_dashboard: "layers",
  concept_deep_dive: "sigma",
  case_study: "book",
  assessment_brief: "target",
  study_path: "list",
};

export function Preset({ block, onNavigate, interactive }: BlockViewProps<PresetBlock>) {
  const preset = block.preset;
  const name = preset?.name ?? "";
  const label = PRESET_LABEL[name] ?? "Vista del curso";
  const title = preset?.title ?? block.meta?.title ?? label;
  const data = preset?.data ?? {};
  const keys = Object.keys(data);

  return (
    <div className="atom atom--preset preset-card">
      <div className="preset-card__icon" aria-hidden="true">
        <Icon name={PRESET_ICON[name] ?? "layers"} size="1.125rem" />
      </div>
      <div className="preset-card__body">
        <span className="preset-card__kind u-label">{label}</span>
        <h3 className="preset-card__title">{title}</h3>
        <p className="preset-card__meta u-mono">
          {name}
          {keys.length > 0 ? ` · ${keys.length} campos` : ""}
        </p>
      </div>
      <button
        type="button"
        className="u-button u-button--primary preset-card__open"
        disabled={!interactive || !preset}
        onClick={() => {
          if (preset) onNavigate?.({ kind: "preset", preset });
        }}
      >
        <Icon name="layers" size="0.75rem" />
        Abrir vista
      </button>
    </div>
  );
}

export default Preset;
